import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from 'src/environments/enviroments';
import {
  ClientUser,
  RegisterUserRequest,
  ReponsePassSii,
  ResponseRegisterUser,
  UpdatePreferences,
} from '../interface/user.interface';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private storageSubject = new BehaviorSubject<string | null>(null);
  private decodedToken: any;

  constructor(private http: HttpClient) {
    this.initializeToken();
    window.addEventListener('storage', (event) => {
      if (event.storageArea === sessionStorage) {
        this.storageSubject.next(event.newValue || '');
      }
    });
  }

  private initializeToken() {
    const tkn = localStorage.getItem('token');
    if (tkn) {
      const jwtHelper = new JwtHelperService();
      this.decodedToken = jwtHelper.decodeToken(tkn);
    }
  }

  private getDecodedToken() {
    if (!this.decodedToken) {
      this.initializeToken();
    }
    return this.decodedToken;
  }

  setToken(token: string) {
    localStorage.setItem('token', token);
    this.initializeToken(); // Actualizamos el token decodificado
  }

  login(request: ClientUser): Observable<any> {
    const user = { userLogin: request };
    const endpoint = `${environment.apiUrl}/Authorization`;
    return this.http.post<any>(endpoint, user);
  }

  registerNewUser(request: RegisterUserRequest): Observable<ResponseRegisterUser> {
    const register = { data: request };
    const endpoint = `${environment.apiUrl}/ClientUser/Register`;
    return this.http.post<ResponseRegisterUser>(endpoint, register);
  }

  validationStatus(pass: number): Observable<ReponsePassSii> {
    const endpoint = `${environment.apiUrl}/Company/ValidationStatus?companyId=${pass}`;
    return this.http.get<ReponsePassSii>(endpoint);
  }

  successForm(form: UpdatePreferences): Observable<any> {
    const endpoint = `${environment.apiUrl}/ClientUser/Register`;
    return this.http.post<any>(endpoint, form);
  }

  registerPreferences(userId: number, preferences: string): Observable<any> {
    const endpoint = `${environment.apiUrl}/ClientUser/UpdatePreferences`;
    const value = { userId, preferences };
    return this.http.post<any>(endpoint, value);
  }

  public changePass(): boolean {
    const decode = this.getDecodedToken();
    return decode && decode['requireChangePassword'] === 'True';
  }

  public getNameIdentifier(): string {
    const decode = this.getDecodedToken();
    return decode ? decode['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] : '';
  }

  public getEmailIdentifier(): string {
    const decode = this.getDecodedToken();
    return decode ? decode['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] : '';
  }

  public getRutUser(): string {
    const decode = this.getDecodedToken();
    return decode ? decode['id'] : '';
  }

  public getUserRoles(): Array<string> {
    const decode = this.getDecodedToken();
    const rolesStr: string = decode ? decode['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] : '';
    return rolesStr ? rolesStr.split(';') : [];
  }

  public getUserPermissions(): Array<string> {
    const decode = this.getDecodedToken();
    const permissionsStr: string = decode ? decode['permissions'] : '';
    return permissionsStr ? permissionsStr.split(';') : [];
  }

  public getUserType(): string {
    const decode = this.getDecodedToken();
    return decode ? decode['userClient'] : '';
  }

  public getCompaniesAssocied(): string {
    const decode = this.getDecodedToken();
    return decode ? decode['companiesAssocied'] : '';
  }

  public ValidateUserPermission(permissionStr: string): boolean {
    const permissions = this.getUserPermissions();
    return permissions.includes(permissionStr);
  }

  watchStorageSession() {
    return this.storageSubject.asObservable();
  }

  setItemSession(key: string, value: string): void {
    sessionStorage.setItem(key, value);
    this.storageSubject.next(value);
  }

  getItemSession(key: string): string | null {
    return sessionStorage.getItem(key);
  }

  removeItemSession(key: string): void {
    sessionStorage.removeItem(key);
    this.storageSubject.next(null);
  }

  hasPermission(toValidatePermission: string): boolean {
    return this.ValidateUserPermission(toValidatePermission);
  }

  hasExpiredToken(): boolean {
    const jwtHelper = new JwtHelperService();
    const token = localStorage.getItem('token');
    return jwtHelper.isTokenExpired(token);
  }
}
